<template>
  <div>
    <v-sheet color="grey-8" class="py-3">
      <div class="d-flex px-4">
        <div class="d-flex align-center" style="width: 800px; max-width: 100%; gap: 8px;">
          <div>搜尋</div>
          <v-text-field
            v-model="searchQuery"
            label="搜尋關鍵字：病患名稱 / 手機 / 看診時間(YYYY-MM-DD) / 項目名稱"
            clearable
            hide-details
            dense
            outlined
            @click:clear="clearSearch"
            @keydown.enter="triggerSearch"
          />
          <v-btn icon color="primary" @click="triggerSearch">
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
        </div>
      </div>
    </v-sheet>
    <v-data-table
      :headers="headers"
      :items="items"
      :options.sync="options"
      :server-items-length="totalItems"
      :loading="loading"
      fixed-header
      height="calc(100vh - 256px)"
      :footer-props="{itemsPerPageOptions: [10, 20, 50, 100]}"
    >
      <template v-slot:item.reserve_start="{ item }">
        {{ convertToTimezone(item.reserve_start, 'Asia/Taipei', 'YYYY-MM-DD HH:mm') }}
      </template>
      <template v-slot:item.sending_progress="{ item }">
        <div v-if="item.messages && item.messages.length">
          <div v-for="(msg, index) in item.messages" :key="msg.id">
            {{ item.tag_name }} - {{ index + 1 }}: {{ convertToTimezone(msg.scheduled_send_at, 'Asia/Taipei', 'YYYY-MM-DD HH:mm') }}
            <span :class="`${statusColor(msg.send_status)}--text`">{{ formatStatus(msg.send_status) }}</span>
          </div>
        </div>
      </template>

      <template v-slot:item.note="{ item }">
        <span>{{ (item.note && item.note.length > 20) ? item.note.substr(0, 20) + '...' : item.note }}</span>
        <v-btn icon color="primary" @click="openCrmNoteDialog(item)">
          <v-icon x-small>mdi-pencil</v-icon>
        </v-btn>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn v-if="isEditable(item)" small outlined color="primary" @click="openEditDialog(item)">
          編輯發送進度
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="editDialog" max-width="700px" scrollable>
      <v-card max-height="500">
        <v-card-title>
          <span class="headline">編輯發送進度</span>
        </v-card-title>
        <v-card-text>
          <div v-if="editableMessages.length" style="width: 100%;" class="overflow-x-auto">
            <div v-for="(msg, index) in editableMessages" :key="msg.id" class="d-flex align-center my-3">
              <span class="mr-2 text-no-wrap">{{ currentItem.tag_name }} - {{ index + 1 }}:</span>

              <!-- EDITING MODE -->
              <template v-if="msg.isEditing">
                <v-menu
                  v-model="datePickerMenus[index]"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="msg.date"
                      label="日期"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      dense
                      outlined
                      hide-details
                      class="mr-2"
                      style="min-width: 150px;"
                    />
                  </template>
                  <v-date-picker
                    v-model="msg.date"
                    scrollable
                    locale="zh"
                    :min="minDate"
                    @input="datePickerMenus[index] = false"
                  />
                </v-menu>

                <v-select
                  v-model="msg.hour"
                  :items="hours"
                  label="時"
                  dense
                  outlined
                  hide-details
                  class="mr-2"
                  style="max-width: 100px;"
                ></v-select>
                <span class="mr-2">:</span>
                <v-select
                  v-model="msg.minute"
                  :items="minutes"
                  label="分"
                  dense
                  outlined
                  hide-details
                  class="mr-2"
                  style="max-width: 100px;"
                ></v-select>

                <v-select
                  v-model="msg.status"
                  :items="editableStatusOptions"
                  dense
                  outlined
                  hide-details
                  style="min-width: 150px; max-width: 150px;"
                ></v-select>
              </template>

              <!-- READ-ONLY MODE -->
              <template v-else>
                <span class="mx-2 font-weight-bold">{{ msg.date }} {{ msg.hour }}:{{ msg.minute }}</span>
                <v-chip small dark :color="statusColor(msg.status)">{{ formatStatus(msg.status) }}</v-chip>
              </template>

              <!-- EDIT ACTION -->
              <v-btn v-if="msg.isPotentiallyEditable" small icon @click="toggleEditMessage(msg)" class="ml-2">
                <v-icon>{{ msg.isEditing ? 'mdi-close-circle' : 'mdi-pencil' }}</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="d-flex flex-column" style="row-gap: 10px;">
          <div style="width: 300px;">
            <v-btn color="tiffany-9" block dark elevation="0" @click="saveChanges">儲存編輯</v-btn>
          </div>
          <div style="width: 300px;">
            <v-btn block outlined color="grey-5" @click="closeEditDialog">取消</v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-if="editingCrmNoteItem" v-model="crmNoteDialog" max-width="500" persistent>
      <v-card>
        <v-card-title>修改院內備註</v-card-title>
        <v-card-text>
          <v-textarea v-model="editingCrmNoteItem.note" hide-details outlined no-resize/>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="blue" text @click="updateCrmNote">確定</v-btn>
          <v-btn text @click="crmNoteDialog = false">取消</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getCustomizeCareSendQueueAPI, batchUpdateCareMessagesAPI, updateCareSendQueueCrmNoteAPI } from "@/api";
import { convertToTimezone } from "@/utils/filters";

export default {
  name: "PendingList",
  data() {
    return {
      searchQuery: '',
      loading: true,
      items: [],
      totalItems: 0,
      options: {},
      headers: [
        { text: '病患姓名', value: 'patient_name', sortable: false },
        { text: '病患手機', value: 'patient_phone', sortable: false },
        { text: '看診時間', value: 'reserve_start', sortable: false },
        { text: '約診醫師', value: 'doctor_name', sortable: false },
        { text: '約診診所', value: 'clinic_name', sortable: false },
        { text: '關懷項目', value: 'tag_name', sortable: false },
        { text: '發送進度', value: 'sending_progress', sortable: false, width: '25%' },
        { text: '院內備註', value: 'note', sortable: false, width: 150 },
        { text: '操作', value: 'actions', sortable: false },
      ],
      crmNoteDialog: false,
      editingCrmNoteItem: null,
      editDialog: false,
      currentItem: null,
      editableMessages: [],
      datePickerMenus: [],
      minDate: new Date().toISOString().substr(0, 10),
      hours: Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')),
      minutes: Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0')),
      statusOptions: [
        { text: '待發送', value: '0' },
        { text: '成功發送', value: '1' },
        { text: '發送失敗', value: '2' },
        { text: '取消發送', value: '3' },
        { text: '發送中', value: '4' },
        { text: '時間已過', value: '5' },
      ],
      editableStatusOptions: [
        { text: '待發送', value: '0' },
        { text: '取消發送', value: '3' },
      ],
    };
  },
  computed: {
    ...mapGetters(["axiosParamData"]),
  },
  watch: {
    options: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
  },
  methods: {
    isEditable(item) {
      if (!item.messages || item.messages.length === 0) {
        return false;
      }
      return item.messages.some(msg => this.$moment(msg.scheduled_send_at).isAfter(this.$moment()) || msg.send_status === '5');
    },
    convertToTimezone,
    statusColor(status) {
      switch(status) {
        case '0': return 'blue-6';
        case '1': return 'green-6';
        case '2': return 'red-6';
        case '3': return 'grey-5';
        case '4': return 'orange-6';
        case '5': return 'red-6';
        default: return 'grey-5';
      }
    },
    formatStatus(status) {
      const found = this.statusOptions.find(option => option.value === status);
      return found ? found.text : '未知';
    },
    async fetchData() {
      this.loading = true;
      try {
        const { page, itemsPerPage } = this.options;
        const params = {
          ...this.axiosParamData,
          page,
          per_page: itemsPerPage,
        };
        if (this.searchQuery && this.searchQuery.trim()) {
          params.q = this.searchQuery.trim();
        }
        const res = await getCustomizeCareSendQueueAPI(params);
        this.items = res.data.data
        this.totalItems = res.data.meta.total;
      } catch (error) {
        console.error("Error fetching pending list:", error);
      } finally {
        this.loading = false;
      }
    },
    openEditDialog(item) {
      this.currentItem = item;
      this.editableMessages = item.messages.map(msg => {
        const taipeiTime = this.$moment(msg.scheduled_send_at).tz('Asia/Taipei');
        const isPast = this.$moment(msg.scheduled_send_at).isBefore(this.$moment());

        return {
          id: msg.id,
          date: taipeiTime.format('YYYY-MM-DD'),
          hour: taipeiTime.format('HH'),
          minute: taipeiTime.format('mm'),
          status: msg.send_status,
          isPotentiallyEditable: !isPast || msg.send_status === '5',
          isEditing: false,
        };
      });
      this.datePickerMenus = Array(item.messages.length).fill(false);
      this.editDialog = true;
    },
    toggleEditMessage(msg) {
      msg.isEditing = !msg.isEditing;
    },
    closeEditDialog() {
      this.editDialog = false;
      this.currentItem = null;
      this.editableMessages = [];
      this.datePickerMenus = [];
    },
    async saveChanges() {
      // Final validation before saving
      for (const msg of this.editableMessages) {
        if (!msg.isEditing) continue;
        const localTime = `${msg.date}T${msg.hour}:${msg.minute}`;
        if (this.$moment(localTime).isBefore(this.$moment())) {
          alert('錯誤：不能將發送時間設定在過去的時間點。');
          return; // Abort save
        }
      }

      const updates = this.editableMessages
        .filter(msg => msg.isEditing)
        .map(msg => {
          const originalMsg = this.currentItem.messages.find(m => m.id === msg.id);
          const localTime = `${msg.date}T${msg.hour}:${msg.minute}`;
          const utcTime = this.$moment.tz(localTime, 'Asia/Taipei').utc().format();

          let newStatus = msg.status;
          if (originalMsg && originalMsg.send_status === '5') {
            newStatus = '0';
          }

          return {
            id: msg.id,
            scheduled_send_at: utcTime,
            send_status: newStatus,
          };
        });

      if (updates.length === 0) {
        alert("沒有已編輯的項目可儲存。");
        return;
      }

      try {
        await batchUpdateCareMessagesAPI({ updates });
        alert('更新成功！');
        this.closeEditDialog();
        await this.fetchData();
      } catch (error) {
        console.error('Error updating care messages:', error);
        alert('更新發送失敗，請稍後再試。');
      }
    },
    triggerSearch() {
      this.options.page = 1;
      this.fetchData();
    },
    clearSearch() {
      this.searchQuery = '';
      this.triggerSearch();
    },
    openCrmNoteDialog(item) {
      this.editingCrmNoteItem = JSON.parse(JSON.stringify(item));
      this.crmNoteDialog = true;
    },
    async updateCrmNote() {
      try {
        await updateCareSendQueueCrmNoteAPI(this.editingCrmNoteItem.group_uuid, {
          ...this.axiosParamData,
          note: this.editingCrmNoteItem.note
        });
        this.crmNoteDialog = false;
        this.editingCrmNoteItem = null;
        await this.fetchData();
      } catch (error) {
        console.error('Error updating crm note:', error);
        alert('更新備註失敗，請稍後再試。');
      }
    },
  },
};
</script>